/**
 * Authentication Service
 * Wrapper for Supabase Auth with custom backend sync
 */
import { createClient, User, Session } from '@supabase/supabase-js'

// Environment variables for Supabase
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || ''
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || ''

// Log auth configuration status for debugging
console.log('[Auth] Supabase configuration:', {
  hasUrl: !!SUPABASE_URL,
  hasKey: !!SUPABASE_ANON_KEY,
  urlPrefix: SUPABASE_URL ? SUPABASE_URL.substring(0, 30) + '...' : 'none',
  keyPrefix: SUPABASE_ANON_KEY ? SUPABASE_ANON_KEY.substring(0, 20) + '...' : 'none'
})

// Create Supabase client (only if configured)
let supabase: ReturnType<typeof createClient> | null = null
try {
  if (SUPABASE_URL && SUPABASE_ANON_KEY && SUPABASE_URL.startsWith('https://')) {
    supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true
      }
    })
    console.log('[Auth] Supabase client created successfully')
  } else {
    console.log('[Auth] Supabase not configured - auth features disabled')
  }
} catch (error) {
  console.error('[Auth] Failed to create Supabase client:', error)
  supabase = null
}

export interface AuthUser {
  id: string
  email: string
  displayName: string | null
  avatarUrl: string | null
}

export interface AuthState {
  user: AuthUser | null
  session: Session | null
  isLoading: boolean
  isConfigured: boolean
}

/**
 * Check if Supabase auth is configured
 */
export function isAuthConfigured(): boolean {
  return !!supabase
}

/**
 * Get the Supabase client instance
 */
export function getSupabaseClient() {
  return supabase
}

/**
 * Get current session
 */
export async function getSession(): Promise<Session | null> {
  if (!supabase) return null

  const { data: { session } } = await supabase.auth.getSession()
  return session
}

/**
 * Get current user
 */
export async function getCurrentUser(): Promise<AuthUser | null> {
  if (!supabase) return null

  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return null

  return {
    id: user.id,
    email: user.email || '',
    displayName: user.user_metadata?.name || user.user_metadata?.full_name || null,
    avatarUrl: user.user_metadata?.avatar_url || user.user_metadata?.picture || null
  }
}

/**
 * Sign in with OAuth provider
 */
export async function signInWithProvider(provider: 'google' | 'github'): Promise<void> {
  if (!supabase) {
    throw new Error('Authentication is not configured')
  }

  const { error } = await supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: `${window.location.origin}/auth/callback`
    }
  })

  if (error) throw error
}

/**
 * Sign out
 */
export async function signOut(): Promise<void> {
  if (!supabase) return

  const { error } = await supabase.auth.signOut()
  if (error) throw error
}

/**
 * Subscribe to auth state changes
 */
export function onAuthStateChange(callback: (user: AuthUser | null, session: Session | null) => void) {
  if (!supabase) {
    // Call immediately with null if not configured
    callback(null, null)
    return { unsubscribe: () => {} }
  }

  const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
    if (session?.user) {
      const authUser: AuthUser = {
        id: session.user.id,
        email: session.user.email || '',
        displayName: session.user.user_metadata?.name || session.user.user_metadata?.full_name || null,
        avatarUrl: session.user.user_metadata?.avatar_url || session.user.user_metadata?.picture || null
      }
      callback(authUser, session)
    } else {
      callback(null, null)
    }
  })

  return { unsubscribe: () => subscription.unsubscribe() }
}

/**
 * Get access token for API calls
 */
export async function getAccessToken(): Promise<string | null> {
  if (!supabase) return null

  const { data: { session } } = await supabase.auth.getSession()
  return session?.access_token || null
}

/**
 * Sync user profile with backend after login
 */
export async function syncUserProfile(session: Session): Promise<void> {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'

  try {
    const response = await fetch(`${baseUrl}/api/users/sync`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({
        display_name: session.user.user_metadata?.name || session.user.user_metadata?.full_name,
        avatar_url: session.user.user_metadata?.avatar_url || session.user.user_metadata?.picture
      })
    })

    if (!response.ok) {
      console.warn('Failed to sync user profile:', await response.text())
    }
  } catch (error) {
    console.warn('Failed to sync user profile:', error)
    // Don't throw - this is non-critical
  }
}

export default {
  isAuthConfigured,
  getSupabaseClient,
  getSession,
  getCurrentUser,
  signInWithProvider,
  signOut,
  onAuthStateChange,
  getAccessToken,
  syncUserProfile
}
